import html2canvas from "html2canvas";
import type {jsPDF} from "jspdf";

// ---------------------------------------------------------------------------
// Types (open for extension — callers pass options / deps instead of branching)
// ---------------------------------------------------------------------------

export type Html2CanvasRenderer = (element: HTMLElement, options?: Parameters<typeof html2canvas>[1]) => Promise<HTMLCanvasElement>;

/**
 * Optional overrides for tests or alternate renderers (IoC).
 */
export type DomSnapshotCaptureDeps = {
    renderHtmlToCanvas?: Html2CanvasRenderer;
};

export type SnapshotCaptureOptions = {
    /** Expand overflow/clip so horizontal scroll regions contribute full scrollWidth. */
    expandOverflowContainers?: boolean;
    /** Replace canvas elements with raster images so html2canvas sees pixels. */
    serializeCharts?: boolean;
    /** Set inline SVG width/height from layout box (helps donut / vector charts). */
    normalizeInlineSvgDimensions?: boolean;
    /** After `document.fonts.ready`, extra delay for async chart paint (ms). */
    chartSettleDelayMs?: number;
    /** Skip fonts.ready + delay (e.g. non-DOM unit tests). */
    skipDocumentSettle?: boolean;
    /**
     * Elements whose top edge is used as PDF page-break hints (DOM, relative to root).
     * `null` disables section-aware paging.
     */
    sectionAnchorSelector?: string | null;
    /** Canvas px: if a page cut lands within this distance of a section anchor, snap above it. */
    sectionSnapGuardPx?: number;
    /** Optional override for html2canvas scale (use 1 for faster/simpler captures). */
    scaleOverride?: number;
};

const defaultSnapshotCaptureOptions = {
    expandOverflowContainers: true,
    serializeCharts: true,
    normalizeInlineSvgDimensions: true,
    chartSettleDelayMs: 150,
    skipDocumentSettle: false,
    sectionAnchorSelector: "[data-export-section]",
    sectionSnapGuardPx: 60,
    scaleOverride: undefined,
} as const;

export type DomSnapshotCaptureResult = {
    canvas: HTMLCanvasElement;
    /** CSS px height used as html2canvas `height` / mapping baseline. */
    domContentHeight: number;
    /** Section (or other) anchor offsets from root top, CSS px, sorted ascending. */
    sectionHeaderOffsetsDom: readonly number[];
};

// ---------------------------------------------------------------------------
// Document settle (fonts + chart paint)
// ---------------------------------------------------------------------------

export async function settleDocumentForSnapshotCapture(chartSettleDelayMs: number = 150): Promise<void> {
    if (typeof document !== "undefined") {
        await document.fonts.ready.catch(() => {});
    }
    await new Promise<void>((r) => setTimeout(r, chartSettleDelayMs));
}

function doubleRaf(): Promise<void> {
    return new Promise((resolve) => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => resolve());
        });
    });
}

function normalizeUnsupportedColorsForCapture(root: HTMLElement): () => void {
    const originals = new Map<Element, Map<string, string>>();
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (ctx === null) {
        return () => {};
    }

    const colorProperties = [
        "color",
        "background-color",
        "border-top-color",
        "border-right-color",
        "border-bottom-color",
        "border-left-color",
        "outline-color",
        "text-decoration-color",
        "caret-color",
        "column-rule-color",
        "fill",
        "stroke",
        "stop-color",
        "flood-color",
        "lighting-color",
    ] as const;

    const normalizeColor = (value: string): string | null => {
        const trimmed = value.trim().toLowerCase();
        if (!trimmed.includes("color(") && !trimmed.includes("oklch(")) {
            return null;
        }
        const prior = ctx.fillStyle;
        ctx.fillStyle = "#000000";
        try {
            ctx.fillStyle = value;
            const normalized = String(ctx.fillStyle);
            ctx.fillStyle = prior;
            if (normalized.length === 0) {
                return null;
            }
            return normalized;
        } catch {
            ctx.fillStyle = prior;
            return null;
        }
    };

    const applyInline = (el: Element, prop: string, value: string) => {
        const style = (el as HTMLElement | SVGElement).style;
        let propMap = originals.get(el);
        if (propMap === undefined) {
            propMap = new Map<string, string>();
            originals.set(el, propMap);
        }
        if (!propMap.has(prop)) {
            propMap.set(prop, style.getPropertyValue(prop));
        }
        style.setProperty(prop, value);
    };

    const nodes: Element[] = [root, ...Array.from(root.querySelectorAll("*"))];
    for (const el of nodes) {
        const computed = getComputedStyle(el);
        for (const prop of colorProperties) {
            const rawValue = computed.getPropertyValue(prop);
            const normalized = normalizeColor(rawValue);
            if (normalized !== null) {
                applyInline(el, prop, normalized);
            }
        }
    }

    return () => {
        for (const [el, props] of originals) {
            const style = (el as HTMLElement | SVGElement).style;
            for (const [prop, oldValue] of props) {
                if (oldValue === "") {
                    style.removeProperty(prop);
                } else {
                    style.setProperty(prop, oldValue);
                }
            }
        }
    };
}

// ---------------------------------------------------------------------------
// Overflow expansion
// ---------------------------------------------------------------------------

/**
 * Temporarily expands scroll/clip containers so nested horizontal overflow is included in
 * scrollWidth (html2canvas otherwise captures only the visible viewport width).
 */
export function expandForSnapshotCapture(root: HTMLElement): () => void {
    const originals = new Map<HTMLElement, Map<string, string>>();

    function touch(el: HTMLElement): Map<string, string> {
        let m = originals.get(el);
        if (m === undefined) {
            m = new Map();
            originals.set(el, m);
        }
        return m;
    }

    function setInline(el: HTMLElement, name: string, value: string) {
        const m = touch(el);
        if (!m.has(name)) {
            m.set(name, el.style.getPropertyValue(name));
        }
        el.style.setProperty(name, value);
    }

    function walk(el: HTMLElement) {
        const cs = getComputedStyle(el);
        const ox = cs.overflowX;
        const blocksX = (ox === "auto" || ox === "scroll" || ox === "hidden" || ox === "clip") && el.scrollWidth > el.clientWidth + 1;
        if (blocksX) {
            setInline(el, "overflow-x", "visible");
            setInline(el, "width", `${el.scrollWidth}px`);
            setInline(el, "max-width", "none");
            setInline(el, "min-width", `${el.scrollWidth}px`);
        }

        const oy = cs.overflowY;
        const blocksY = (oy === "auto" || oy === "scroll") && el.scrollHeight > el.clientHeight + 1;
        if (blocksY) {
            setInline(el, "overflow-y", "visible");
            setInline(el, "height", `${el.scrollHeight}px`);
            setInline(el, "max-height", "none");
        }

        for (const child of el.children) {
            if (child instanceof HTMLElement) {
                walk(child);
            }
        }
    }

    setInline(root, "overflow", "visible");
    setInline(root, "max-width", "none");

    walk(root);

    const fullW = root.scrollWidth;
    const fullH = root.scrollHeight;
    setInline(root, "width", `${fullW}px`);
    setInline(root, "min-width", `${fullW}px`);
    setInline(root, "height", `${fullH}px`);

    return () => {
        for (const [el, props] of originals) {
            for (const [name, prev] of props) {
                if (prev === "") {
                    el.style.removeProperty(name);
                } else {
                    el.style.setProperty(name, prev);
                }
            }
        }
    };
}

// ---------------------------------------------------------------------------
// Chart serialization (canvas → img, SVG dimensions)
// ---------------------------------------------------------------------------

export type SerializeChartsOptions = {
    normalizeSvg?: boolean;
    /** Skip tiny SVGs (icons) — only normalize if both dimensions exceed this. */
    minSvgBoxPx?: number;
};

/**
 * Call immediately before html2canvas; invoke the returned function after capture (LIFO restore).
 */
export function serializeChartsForCapture(root: HTMLElement, options: SerializeChartsOptions = {}): () => void {
    const {normalizeSvg = true, minSvgBoxPx = 20} = options;
    const restorers: (() => void)[] = [];

    root.querySelectorAll<HTMLCanvasElement>("canvas").forEach((canvas) => {
        if (canvas.width === 0 || canvas.height === 0) {
            return;
        }
        let dataUrl: string;
        try {
            dataUrl = canvas.toDataURL("image/png");
        } catch {
            return;
        }
        const img = document.createElement("img");
        img.src = dataUrl;
        const w = canvas.offsetWidth > 0 ? canvas.offsetWidth : canvas.width;
        const h = canvas.offsetHeight > 0 ? canvas.offsetHeight : canvas.height;
        img.style.cssText = `display:block;width:${w}px;height:${h}px;max-width:none;max-height:none`;
        canvas.replaceWith(img);
        restorers.push(() => {
            img.replaceWith(canvas);
        });
    });

    if (normalizeSvg) {
        const svgs = Array.from(root.querySelectorAll<SVGSVGElement>("svg")).filter((svg) => {
            let p: Element | null = svg.parentElement;
            while (p !== null && p !== root) {
                if (p instanceof SVGSVGElement) {
                    return false;
                }
                p = p.parentElement;
            }
            return true;
        });

        svgs.forEach((svg) => {
            const rect = svg.getBoundingClientRect();
            if (rect.width < minSvgBoxPx || rect.height < minSvgBoxPx) {
                return;
            }
            const oldW = svg.getAttribute("width");
            const oldH = svg.getAttribute("height");
            svg.setAttribute("width", String(rect.width));
            svg.setAttribute("height", String(rect.height));
            restorers.push(() => {
                if (oldW !== null) {
                    svg.setAttribute("width", oldW);
                } else {
                    svg.removeAttribute("width");
                }
                if (oldH !== null) {
                    svg.setAttribute("height", oldH);
                } else {
                    svg.removeAttribute("height");
                }
            });
        });
    }

    return () => {
        for (let i = restorers.length - 1; i >= 0; i--) {
            restorers[i]!();
        }
    };
}

// ---------------------------------------------------------------------------
// Section anchors (DOM space, relative to root top)
// ---------------------------------------------------------------------------

export function elementTopRelativeToRoot(root: HTMLElement, el: HTMLElement): number {
    const rootRect = root.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    return elRect.top - rootRect.top + root.scrollTop;
}

function collectSectionHeaderOffsetsDom(root: HTMLElement, selector: string): number[] {
    if (selector === "") {
        return [];
    }
    return Array.from(root.querySelectorAll(selector))
        .map((node) => elementTopRelativeToRoot(root, node as HTMLElement))
        .filter((y) => y >= 0 && Number.isFinite(y))
        .sort((a, b) => a - b);
}

function mapDomOffsetsToCanvasPx(offsetsDom: readonly number[], domH: number, canvasH: number): number[] {
    const h = Math.max(domH, 1);
    const k = canvasH / h;
    return offsetsDom.map((y) => y * k);
}

// ---------------------------------------------------------------------------
// Scale + html2canvas
// ---------------------------------------------------------------------------

export function pickSnapshotScale(w: number, h: number): number {
    const maxDim = Math.max(w, h);
    let scale = maxDim > 12000 ? 1 : maxDim > 8000 ? 1.25 : 2;
    const maxEdgePx = 8192;
    const widthBound = maxEdgePx / Math.max(w, 1);
    const heightBound = maxEdgePx / Math.max(h, 1);
    scale = Math.min(scale, widthBound, heightBound);
    // Keep a tiny lower bound for pathological dashboards, but never exceed canvas-edge limits.
    return Math.max(0.1, scale);
}

function replaceUnsupportedCssColorFunctions(input: string): string {
    return input.replace(/oklch\([^()]*\)/gi, "rgb(0, 0, 0)").replace(/color\([^()]*\)/gi, "rgb(0, 0, 0)");
}

function sanitizeUnsupportedColorFunctionsInClone(doc: Document): void {
    doc.querySelectorAll("style").forEach((styleEl) => {
        const css = styleEl.textContent;
        if (css.includes("color(") === false && css.includes("oklch(") === false) {
            return;
        }
        styleEl.textContent = replaceUnsupportedCssColorFunctions(css);
    });

    doc.querySelectorAll<HTMLElement | SVGElement>("[style]").forEach((el) => {
        const inline = el.getAttribute("style");
        if (inline === null || (inline.includes("color(") === false && inline.includes("oklch(") === false)) {
            return;
        }
        el.setAttribute("style", replaceUnsupportedCssColorFunctions(inline));
    });
}

/**
 * Full pipeline: settle → expand → serialize charts → measure section anchors & size → html2canvas → restore.
 * Composable steps are exported above for custom orchestration.
 */
export async function captureDomSubtreeToCanvas(
    root: HTMLElement,
    options: SnapshotCaptureOptions = {},
    deps: DomSnapshotCaptureDeps = {},
): Promise<DomSnapshotCaptureResult> {
    const o = {...defaultSnapshotCaptureOptions, ...options};
    const renderer = deps.renderHtmlToCanvas ?? html2canvas;

    const sectionSelector =
        options.sectionAnchorSelector === null ? "" : (options.sectionAnchorSelector ?? defaultSnapshotCaptureOptions.sectionAnchorSelector);

    if (!o.skipDocumentSettle) {
        await settleDocumentForSnapshotCapture(o.chartSettleDelayMs);
    }

    let restoreExpand: (() => void) | undefined;
    if (o.expandOverflowContainers) {
        restoreExpand = expandForSnapshotCapture(root);
    }

    await doubleRaf();

    let restoreSerialize: (() => void) | undefined;
    if (o.serializeCharts) {
        restoreSerialize = serializeChartsForCapture(root, {
            normalizeSvg: o.normalizeInlineSvgDimensions,
        });
    }

    const restoreColorNormalization = normalizeUnsupportedColorsForCapture(root);

    await doubleRaf();

    const sectionHeaderOffsetsDom = sectionSelector === "" ? [] : collectSectionHeaderOffsetsDom(root, sectionSelector);

    const w = Math.max(root.scrollWidth, root.offsetWidth);
    const h = Math.max(root.scrollHeight, root.offsetHeight);
    const domContentHeight = h;
    const scale = o.scaleOverride ?? pickSnapshotScale(w, h);

    const renderOptions: Parameters<typeof html2canvas>[1] = {
        scale,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
        width: w,
        height: h,
        windowWidth: w,
        windowHeight: h,
        scrollX: 0,
        scrollY: 0,
    };

    try {
        const canvas = await renderer(root, renderOptions);
        return {canvas, domContentHeight, sectionHeaderOffsetsDom};
    } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        const unsupportedColorFunction = message.includes("unsupported color function") && (message.includes('"color"') || message.includes('"oklch"'));
        if (unsupportedColorFunction !== true) {
            throw error;
        }

        const canvas = await renderer(root, {
            ...renderOptions,
            // Let the browser paint cloned DOM directly; less strict CSS parsing than default path.
            foreignObjectRendering: true,
            onclone: (clonedDocument: Document) => {
                sanitizeUnsupportedColorFunctionsInClone(clonedDocument);
            },
        });
        return {canvas, domContentHeight, sectionHeaderOffsetsDom};
    } finally {
        restoreColorNormalization();
        restoreSerialize?.();
        restoreExpand?.();
        await doubleRaf();
    }
}

// ---------------------------------------------------------------------------
// PDF paging (section-aware cuts)
// ---------------------------------------------------------------------------

function adjustPageCutPx(srcY: number, proposedEnd: number, totalH: number, sectionYsCanvas: readonly number[], guardPx: number, minAdvancePx: number): number {
    let end = Math.min(proposedEnd, totalH);
    if (end <= srcY) {
        return Math.min(srcY + minAdvancePx, totalH);
    }
    if (end >= totalH) {
        return totalH;
    }

    for (const sy of sectionYsCanvas) {
        if (sy <= srcY) {
            continue;
        }
        if (Math.abs(sy - end) < guardPx && sy - 4 > srcY) {
            end = Math.min(end, sy - 4);
        }
    }

    for (const sy of sectionYsCanvas) {
        if (sy > srcY && sy < end) {
            const before = sy - 4;
            if (before > srcY + 8) {
                end = Math.min(end, before);
            }
        }
    }

    if (end <= srcY) {
        end = Math.min(srcY + minAdvancePx, totalH);
    }

    return end;
}

/**
 * Adds a full-width raster image to jsPDF with page breaks, optionally snapping cuts near
 * `[data-export-section]` (or similar) so headers are not split.
 */
export function addCanvasToPdfPaged(
    doc: jsPDF,
    canvas: HTMLCanvasElement,
    marginMm: number,
    startYMm: number,
    paging?: {
        domContentHeight: number;
        sectionHeaderOffsetsDom: readonly number[];
        sectionSnapGuardPx?: number;
    },
) {
    const pageHeightMm = doc.internal.pageSize.getHeight();
    const pageWidthMm = doc.internal.pageSize.getWidth();
    const maxW = pageWidthMm - 2 * marginMm;

    const imgWpx = canvas.width;
    const imgHpx = canvas.height;
    const mmPerPx = maxW / imgWpx;

    const guardPx = paging?.sectionSnapGuardPx ?? 60;
    const sectionYsCanvas =
        paging !== undefined && paging.sectionHeaderOffsetsDom.length > 0
            ? mapDomOffsetsToCanvasPx(paging.sectionHeaderOffsetsDom, Math.max(paging.domContentHeight, 1), imgHpx)
            : [];

    let srcY = 0;
    let pageStartYMm = startYMm;
    let remainingPageMm = pageHeightMm - pageStartYMm - marginMm;
    const minAdvancePx = 24;

    while (srcY < imgHpx - 0.5) {
        const maxChunkPx = remainingPageMm / mmPerPx;
        let cut = adjustPageCutPx(srcY, srcY + maxChunkPx, imgHpx, sectionYsCanvas, guardPx, minAdvancePx);
        let chunkPx = cut - srcY;
        if (chunkPx < 1) {
            cut = Math.min(srcY + Math.max(maxChunkPx, minAdvancePx), imgHpx);
            chunkPx = cut - srcY;
        }

        const sliceH = Math.ceil(chunkPx);
        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = imgWpx;
        sliceCanvas.height = sliceH;
        const ctx = sliceCanvas.getContext("2d");
        if (ctx === null) {
            break;
        }
        ctx.drawImage(canvas, 0, srcY, imgWpx, chunkPx, 0, 0, imgWpx, chunkPx);
        const sliceData = sliceCanvas.toDataURL("image/png");
        const sliceH_mm = chunkPx * mmPerPx;
        doc.addImage(sliceData, "PNG", marginMm, pageStartYMm, maxW, sliceH_mm);

        srcY = cut;
        if (srcY < imgHpx - 0.5) {
            doc.addPage();
            pageStartYMm = marginMm;
            remainingPageMm = pageHeightMm - 2 * marginMm;
        }
    }
}
